import type {
  ProgramDayExercise,
  TrainingBlock,
} from "@/api/db/schema/program-days";

export interface TrainingBlockGroup<T> {
  block: TrainingBlock;
  exercises: T[];
}

export type TrainingDayItem<T> =
  | { kind: "exercise"; exercise: T }
  | ({ kind: "block" } & TrainingBlockGroup<T>);

export function groupTrainingDayItems<
  T extends Pick<ProgramDayExercise, "trainingBlock">,
>(exercises: T[]): TrainingDayItem<T>[] {
  const items: TrainingDayItem<T>[] = [];

  for (const exercise of exercises) {
    const block = exercise.trainingBlock;
    const previous = items.at(-1);

    if (
      block &&
      previous?.kind === "block" &&
      previous.block.blockId === block.blockId
    ) {
      previous.exercises.push(exercise);
      continue;
    }

    if (block) {
      items.push({ kind: "block", block, exercises: [exercise] });
    } else {
      items.push({ kind: "exercise", exercise });
    }
  }

  return items;
}

export function getTrainingBlockLabels<
  T extends Pick<ProgramDayExercise, "trainingBlock">,
>(exercises: T[]) {
  const counters: Record<TrainingBlock["type"], number> = {
    superset: 0,
    amrap: 0,
  };
  const labels = new Map<string, string>();

  for (const item of groupTrainingDayItems(exercises)) {
    if (item.kind !== "block" || labels.has(item.block.blockId)) continue;
    const number = counters[item.block.type]++;
    const suffix = String.fromCharCode(65 + number);
    labels.set(
      item.block.blockId,
      `${item.block.type === "superset" ? "Süperset" : "AMRAP"} ${suffix}`,
    );
  }

  return labels;
}

export function getTrainingBlockTitle(
  block: TrainingBlock,
  generatedLabel: string,
) {
  return block.title?.trim()
    ? `${generatedLabel} · ${block.title.trim()}`
    : generatedLabel;
}

export function formatTrainingBlockSummary(block: TrainingBlock) {
  if (block.type === "superset") {
    return `${block.rounds} tur · Hareketler arası ${block.transitionRestSeconds} sn · Tur sonu ${block.roundRestSeconds} sn`;
  }

  const minutes = Math.floor(block.durationSeconds / 60);
  const seconds = block.durationSeconds % 60;
  const duration =
    seconds > 0 ? `${minutes} dk ${seconds} sn` : `${minutes} dk`;
  return `${duration} · Hareketleri sırayla tamamla`;
}
